/**
 * Dummy data — FAQ / 문의
 * API_DOCS: 12. 문의 & FAQ (서비스) / 28. 관리자 - 문의/FAQ
 *
 * FaqResponse:
 *   id(int), category(string), question(string?), answer(string?),
 *   sort_order(int), is_active(boolean)
 *
 * InquiryResponse:
 *   id(int), user_id(int?), title(string?), content(string?),
 *   answer(string?), status(string="pending"|"answered"), created_at(datetime)
 */

const faqs = [
  { id: 1, category: '예약', question: '예약은 어떻게 하나요?', answer: '홈페이지에서 원하시는 서비스를 선택하신 후 예약 양식을 작성해 주세요.', sort_order: 1, is_active: true },
  { id: 2, category: '예약', question: '예약 취소는 언제까지 가능한가요?', answer: '이용일 기준 48시간 전까지 무료 취소가 가능합니다.', sort_order: 2, is_active: true },
  { id: 3, category: '결제', question: '어떤 결제 수단을 사용할 수 있나요?', answer: '신용카드, 체크카드, 계좌이체를 지원합니다.', sort_order: 1, is_active: true },
  { id: 4, category: '서비스', question: '반려동물도 탑승 가능한가요?', answer: '네, 반려동물 전용 서비스를 이용하시면 됩니다.', sort_order: 1, is_active: true },
  { id: 5, category: '서비스', question: '차량 종류는 무엇인가요?', answer: '현대 스타리아 라운지를 운행하고 있습니다.', sort_order: 2, is_active: true },
];

const inquiries = [
  { id: 1, user_id: 1, title: '예약 변경 문의', content: '3월 15일 예약을 3월 17일로 변경하고 싶습니다.', answer: null, status: 'pending', created_at: '2026-03-01T10:00:00' },
  { id: 2, user_id: 3, title: '영수증 재발급 요청', content: '2월 7일 이용분 영수증을 재발급 받고 싶습니다.', answer: '이메일로 재발급 처리해드렸습니다.', status: 'answered', created_at: '2026-02-20T14:30:00' },
  { id: 3, user_id: 2, title: '서비스 범위 문의', content: '경기도 양평까지도 픽업 가능한가요?', answer: '네, 경기도 전 지역 픽업 가능합니다. 지역에 따라 추가 요금이 발생할 수 있습니다.', status: 'closed', created_at: '2026-02-10T09:00:00' },
];

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/* ── 서비스 (유저용) ── */

/** GET /api/faq/categories */
export const getFaqCategories = async () => {
  await delay(100);
  const cats = [...new Set(faqs.filter((f) => f.is_active).map((f) => f.category))];
  return [
    { id: 'all', label: '자주하는 질문' },
    ...cats.map((c) => ({ id: c, label: c })),
  ];
};

/** GET /api/faq (query: category) */
export const getFaqs = async (params = {}) => {
  await delay(300);
  let filtered = faqs.filter((f) => f.is_active);
  if (params.category) filtered = filtered.filter((f) => f.category === params.category);
  return filtered;
};

/** GET /api/faq/:id */
export const getFaqById = async (id) => {
  await delay(200);
  const found = faqs.find((f) => f.id === Number(id));
  if (!found) throw new Error('FAQ를 찾을 수 없습니다');
  return found;
};

/** POST /api/inquiries */
export const createInquiry = async (data) => {
  await delay(400);
  const newInq = { id: inquiries.length + 1, user_id: 1, title: data.title, content: data.content, answer: null, status: 'pending', created_at: new Date().toISOString() };
  inquiries.push(newInq);
  return newInq;
};

/** GET /api/inquiries */
export const getInquiries = async (params = {}) => {
  await delay(300);
  return inquiries;
};

/** GET /api/inquiries/:id */
export const getInquiryById = async (id) => {
  await delay(200);
  const found = inquiries.find((i) => i.id === Number(id));
  if (!found) throw new Error('문의를 찾을 수 없습니다');
  return found;
};

/* ── 관리자 ── */

/** GET /api/admin/faq */
export const getAdminFaqs = async (params = {}) => {
  await delay(300);
  return faqs;
};

/** POST /api/admin/faq */
export const createFaq = async (data) => {
  await delay(400);
  const newFaq = { id: faqs.length + 1, category: data.category || 'general', question: data.question, answer: data.answer, sort_order: faqs.length + 1, is_active: true };
  faqs.push(newFaq);
  return newFaq;
};

/** PUT /api/admin/faq/:id */
export const updateFaq = async (id, data) => {
  await delay(400);
  const idx = faqs.findIndex((f) => f.id === Number(id));
  if (idx === -1) throw new Error('FAQ를 찾을 수 없습니다');
  faqs[idx] = { ...faqs[idx], ...data };
  return faqs[idx];
};

/** DELETE /api/admin/faq/:id */
export const deleteFaq = async (id) => {
  await delay(300);
  const idx = faqs.findIndex((f) => f.id === Number(id));
  if (idx === -1) throw new Error('FAQ를 찾을 수 없습니다');
  faqs.splice(idx, 1);
  return { message: 'FAQ가 삭제되었습니다' };
};

/** GET /api/admin/inquiries (페이지네이션) */
export const getAdminInquiries = async (params = {}) => {
  await delay(300);
  const page = params.page || 1;
  const size = params.size || 20;
  const start = (page - 1) * size;
  return {
    items: inquiries.slice(start, start + size),
    total: inquiries.length,
    page,
    size,
    pages: Math.ceil(inquiries.length / size),
  };
};

/** GET /api/admin/inquiries/:id */
export const getAdminInquiryById = async (id) => {
  await delay(200);
  const found = inquiries.find((i) => i.id === Number(id));
  if (!found) throw new Error('문의를 찾을 수 없습니다');
  return found;
};

/** PUT /api/admin/inquiries/:id (답변) */
export const answerInquiry = async (id, data) => {
  await delay(400);
  const found = inquiries.find((i) => i.id === Number(id));
  if (!found) throw new Error('문의를 찾을 수 없습니다');
  found.answer = data.answer;
  found.status = 'answered';
  return found;
};
